/**
 * Parametric constraint data model (Faz 6 — OpenCADStudio parity Y1/Y2).
 *
 * Mirrors the OCS parametric model (parametric_constraints.rs) adapted to
 * yap's entity ledger: a single document-scoped ConstraintSet holding
 * geometric + dimensional constraint records, a named-parameter table and
 * the derived dimension binding cache.
 *
 * Marker convention (OCS GsMarker, verbatim):
 *   0/1               line endpoints (start/end)
 *   -2                bounded-curve midpoint
 *   -3                circle/arc center
 *   -1_000_000 - i    polyline segment i start vertex
 *   -2_000_000 - i    polyline segment i midpoint
 *   -3_000_000 - i    polyline segment i (arc) center
 *   -6                text baseline (resolved read-only)
 * yap extension (no OCS collision):
 *   -7                circle/arc radius handle (center + (r, 0)) — the
 *                     particle used to drive Radius/Diameter/Tangent.
 */

import type { ParameterTable } from './parameters';

export interface ConstraintRef {
    /** Entity ledger id (`cad:...`). */
    entityId: string;
    /** OCS sub-point marker (see module docs). Whole-entity refs use -3 for
     *  radial citizens or the resolved endpoint marker for lines. */
    marker: number;
}

/** OCS `DrivingValue`: a literal number or a named parameter reference. */
export type DrivingValue =
    | { literal: number }
    | { named: string };

export interface ConstraintRecord {
    /** Stable id, e.g. 'gc1' / 'dc1' (OCS next_id). */
    id: string;
    kind: import('./kinds').ConstraintKind;
    refs: ConstraintRef[];
    /** Dimensional driving value; geometric constraints leave this unset. */
    driving?: DrivingValue;
    /** Last measured driving value (dimensional reporting + dimension sync). */
    value?: number;
    /** Disabled constraints are recorded but skipped by the solver. */
    enabled: boolean;
    /** Per-constraint glyph visibility (GCHIDE/GCSHOW). */
    hidden?: boolean;
    /** Fixed constraints: the pinned world points at recording time, in ref
     *  order — every solve re-bases the pinned particles on these so MOVE
     *  cannot drag a Fixed object away (OCS: Fixed points are not drivers). */
    fixedPoints?: number[][];
}

export interface ConstraintSet {
    version: 1;
    constraints: ConstraintRecord[];
    nextId: number;
    parameters: ParameterTable;
    /** Derived cache (OCS set.dimensions): constraint id -> dimension entity. */
    dimensions: Record<string, string>;
    /** Constraint ids the solver could not satisfy (derived, not persisted). */
    conflicts: string[];
}

/** Geometric constraint kinds (Y1). */
export type GeometricKind =
    | 'Coincident'
    | 'Horizontal'
    | 'Vertical'
    | 'Parallel'
    | 'Perpendicular'
    | 'Tangent'
    | 'Smooth'
    | 'Concentric'
    | 'Equal'
    | 'Fixed'
    | 'PointOnCurve'
    | 'Symmetric'
    | 'Midpoint'
    | 'Colinear';

/** Dimensional constraint kinds (Y2). */
export type DimensionalKind =
    | 'Distance'
    | 'DistanceX'
    | 'DistanceY'
    | 'Angle'
    | 'Radius'
    | 'Diameter';

/** Marker constants (OCS convention + documented yap extension). */
export const MARKER_LINE_START = 0;
export const MARKER_LINE_END = 1;
export const MARKER_MIDPOINT = -2;
export const MARKER_CENTER = -3;
export const MARKER_RADIUS = -7;
export const POLYLINE_SEGMENT_BASE = -1_000_000;
export const POLYLINE_MIDPOINT_BASE = -2_000_000;
export const POLYLINE_CENTER_BASE = -3_000_000;

/** Versioned payload persisted into the .yap cadScene envelope. Absent or
 *  unknown payloads fall back to an empty set (backwards compatibility). */
export interface ConstraintPayload {
    version: 1;
    constraints: ConstraintRecord[];
    nextId: number;
    parameters: ParameterTable;
    dimensions: Record<string, string>;
    /** Per-constraint glyph visibility snapshot. */
    hidden?: Record<string, boolean>;
}

/** AUTOCONSTRAIN settings (OCS AutoConstrainSettings). */
export interface AutoConstrainSettings {
    /** Require the tangent point to lie on the line segment. */
    tangentMustSharePoint: boolean;
    /** Require perpendicular pairs to actually intersect. */
    perpendicularMustIntersect: boolean;
    /** Endpoint-coincident snapping tolerance (drawing units). */
    distanceTolerance: number;
    /** Angle tolerance in degrees for parallel/perpendicular/horizontal. */
    angleToleranceDeg: number;
    /** Equal inference (OCS: off by default). */
    equalEnabled: boolean;
}

/** OCS Coincident scan epsilon (COINCIDENT_EPSILON_SQ = 1e-12). */
export const COINCIDENT_EPSILON = 1e-6;

export const DEFAULT_AUTOCONSTRAIN_SETTINGS: AutoConstrainSettings = {
    tangentMustSharePoint: true,
    perpendicularMustIntersect: true,
    distanceTolerance: 0.05,
    angleToleranceDeg: 1.0,
    equalEnabled: false,
};
